import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAlert } from 'react-alert';
import {
  TabContent,
  TabPane,
  Form,
  Row,
  Col,
  FormGroup,
  Label,
  CustomInput,
} from 'reactstrap';
import {
  ContAffiliation,
  H5Styled,
  ContFilterYear,
  BtnOutlineOrange,
  BtnOutlineGreen,
  ContPaginationPage,
} from '../../../styled-components';
import SearchInput from '../../../commons/SearchInput';
import generateMonthOptions from '../../../lib/helpers/generateMonthOptions';
import CSVEnergyRevenuesExport from '../FinancialReports/EnergyDetails/CSVEnergyRevenuesExport';
import EnergyRoyaltyTable from '../FinancialReports/EnergyDetails/EnergyRoyaltyTable';
import TotalEnergyRevenuesRow from '../FinancialReports/EnergyDetails/TotalTrEnergyRevenue';
import generateYearOptions from '../../../lib/helpers/generateYearOptions';
import { useAuth } from '../../../commons/Auth';
import { isCheckable } from '../../../lib/helpers/isCheckboxCheckable';
import variables from '../../../variables';
import ReportingModal from '../../../commons/ReportingModal';
import generatePaginationOptions from '../../../lib/helpers/generatePaginationOptions';
import moment from 'moment';

const VarRevEnergyDetailPresentational = ({
  data = {},
  profile,
  total,
  query,
  onFilter,
  onAddBonus,
  profileType = '',
}) => {
  const [t] = useTranslation('CONTACTS');
  const [{ newIsAdmin }] = useAuth();
  const { categories = {} } = data;
  const { variableRevenuesEnergy = {} } = categories;
  const [amount, setAmount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [selectAll, setSelectAll] = useState(false);
  const alert = useAlert();
  const { data: dataRevEnergy = [] } = variableRevenuesEnergy;

  const origin = window.location.origin;
  let hash = window.location.hash.split('/');
  hash.pop();
  const prevPath =
    origin +
    '/' +
    hash.join('/') +
    `${query.year ? '?year=' + query.year : ''}`;

  const getVariableRevenuesTotals = (dataRev) => {
    const cashedTotals = dataRev.reduce((prev, cur) => {
      const { payments } = cur || {};
      return (
        prev +
        (payments || []).reduce((acc, payment) => {
          return acc + ((payment && payment.gross) || 0);
        }, 0)
      );
    }, 0);
    const contractValueTotals = dataRev.reduce(
      (prev, cur) => prev + cur.contract_value,
      0
    );
    const netTotals = dataRev.reduce((prev, cur) => prev + cur.net, 0);
    const grossTotals = dataRev.reduce((prev, cur) => prev + cur.gross, 0);
    const extraShareTotals = dataRev.reduce(
      (prev, cur) => prev + cur.extraShare,
      0
    );
    const adminShareTotals = dataRev.reduce(
      (prev, cur) => prev + cur.adminShare,
      0
    );
    const earnedAdminShareTotals = dataRev.reduce(
      (prev, cur) => prev + cur.earnedAdminShare,
      0
    );
    const billableAdminShareTotals = dataRev.reduce(
      (prev, cur) => prev + cur.billableAdminShare,
      0
    );

    console.log('earnedAdminShareTotals:', earnedAdminShareTotals);
    console.log('billableAdminShareTotals:', earnedAdminShareTotals);
    return {
      cashedTotals,
      netTotals,
      grossTotals,
      contractValueTotals,
      extraShareTotals,
      adminShareTotals,
      earnedAdminShareTotals,
      billableAdminShareTotals,
    };
  };
  const getRemainingIafAmount = ({ billableAdminShare = 0, iafs = [] }) =>
    Number(billableAdminShare || 0) -
    iafs.reduce((total, { amount = 0 }) => total + Number(amount || 0), 0);

  const getSelectedRows = (selectAllRows = false) =>
    dataRevEnergy.filter(
      (row) =>
        (row.checked || selectAllRows) && getRemainingIafAmount(row) !== 0
    );

  const getAmountToIssue = (rows) =>
    +rows
      .reduce((total, row) => total + getRemainingIafAmount(row), 0)
      .toFixed(2);

  const toggle = ({ selectAll: toggleSelectAll = false } = {}) => {
    if (isOpen) {
      setSelectAll(false);
      return setIsOpen(false);
    }

    setSelectAll(toggleSelectAll);

    const checkedRows = getSelectedRows(toggleSelectAll);

    if (checkedRows.length) {
      const amountToIssue = getAmountToIssue(checkedRows);
      if (!(amountToIssue > 0)) {
        alert.error(t('ROYALTY:IAF_AMOUNT_MUST_BE_POSITIVE'));
        return;
      }

      setAmount(amountToIssue);
      setIsOpen(!isOpen);
    } else {
      const message = toggleSelectAll
        ? t('ROYALTY:NO_ROW_IAF_SELECTABLE')
        : t('ROYALTY:SELECT_AT_LEAST_ONE');
      alert.error(message);
    }
  };

  const onSubmit = (formData) => {
    const checkedRows = getSelectedRows(selectAll);
    const amountToIssue = getAmountToIssue(checkedRows);
    if (!(amountToIssue > 0)) {
      alert.error(t('ROYALTY:IAF_AMOUNT_MUST_BE_POSITIVE'));
      return;
    }

    const signingDateYear = moment(
      checkedRows[0]?.contract_signing_date
    ).year();
    for (const row of checkedRows) {
      const rowYear = moment(row.contract_signing_date).year();
      if (rowYear !== signingDateYear) {
        alert.error(
          'I contratti selezionati presentano anni di creazione differenti.'
        );
        toggle();
        return;
      }
    }

    const data = {
      ...formData,
      accountability: signingDateYear,
      iafType: 'ENERGY',
      contracts: checkedRows.map(
        ({
          _id,
          admin,
          condo,
          earnedAdminShare,
          adminShare,
          extraShare,
          date,
          number,
          type,
          contract_signing_date,
        }) => ({
          _id,
          admin,
          condo,
          earnedAdminShare,
          adminShare,
          net: extraShare,
          date,
          number,
          type,
          contract_signing_date: new Date(contract_signing_date),
        })
      ),
      amount: amountToIssue,
    };
    onAddBonus(data);
    toggle();
  };

  return (
    <Form onSubmit={(e) => e.preventDefault()}>
      <TabContent activeTab={'1'}>
        <TabPane tabId="1">
          <ContFilterYear>
            <Row>
              <Col xs="6">
                <Label>{t('TITLE_FILTER_YEAR')}</Label>
              </Col>
              <Col xs="6">
                <FormGroup>
                  <CustomInput
                    type="select"
                    id="selectYear"
                    name="year"
                    value={query && query.year}
                    onChange={({ target: { value: year } }) =>
                      onFilter({ year })
                    }
                  >
                    {generateYearOptions()}
                  </CustomInput>
                </FormGroup>
              </Col>
            </Row>
          </ContFilterYear>
          <div
            style={{
              fontWeight: 700,
              color: '#333333',
              marginBottom: '20px',
              fontSize: '20px',
            }}
          >
            <a
              style={{
                color: variables.primary,
              }}
              href={prevPath}
            >
              Rendicontazioni
            </a>{' '}
            / <a>{t('ENERGY:ROYALTIES_TITLE')}</a>
          </div>
          <ContAffiliation key={2}>
            <Row>
              <Col xs="6">
                <H5Styled>{t('ENERGY:ROYALTIES_TITLE')}</H5Styled>
              </Col>
              <Col xs="6" className="text-right">
                {newIsAdmin && profile.type !== 'UTILIZER' && (
                  <BtnOutlineGreen
                    type="button"
                    onClick={toggle}
                    style={{ marginRight: '16px' }}
                  >
                    {t('ROYALTY:INVITE_TO_INVOICE')}
                  </BtnOutlineGreen>
                )}
                {newIsAdmin && profile.type !== 'UTILIZER' && (
                  <BtnOutlineGreen
                    type="button"
                    onClick={() => toggle({ selectAll: true })}
                    style={{ marginRight: '16px' }}
                  >
                    {t('ROYALTY:INVITE_TO_INVOICE_ALL')}
                  </BtnOutlineGreen>
                )}
              </Col>
            </Row>
            <br />
            <Row className="compensi-variabili-resp">
              <Col sm="6" md="6">
                <SearchInput
                  query={{ search: query && query.energyPod }}
                  onFilter={({ search }) => onFilter({ energyPod: search })}
                  label={t('SEARCH:POD_PDR')}
                />
              </Col>
              <Col sm="6" md="6">
                <SearchInput
                  query={{ search: query && query.energyCondoVat }}
                  onFilter={({ search }) =>
                    onFilter({ energyCondoVat: search })
                  }
                  label={t('SEARCH:CLIENT_VAT_CODE')}
                  autoFocus={false}
                />
              </Col>
              <Col sm="6" md="6">
                <ContFilterYear className="no-width">
                  <Row>
                    <Col xs="6">
                      <Label>{t('COMMON:TITLE_FILTER_ACC_YEAR')}</Label>
                    </Col>
                    <Col xs="6">
                      <FormGroup>
                        <CustomInput
                          type="select"
                          id="selectEnergyAccountabilityYear"
                          name="energyAccountabilityYear"
                          value={query && query.energyAccountabilityYear}
                          onChange={({
                            target: { value: energyAccountabilityYear },
                          }) => {
                            onFilter({ energyAccountabilityYear });
                          }}
                        >
                          {generateYearOptions(true)}
                        </CustomInput>
                      </FormGroup>
                    </Col>
                  </Row>
                </ContFilterYear>
              </Col>
              <Col sm="6" md="6">
                <ContFilterYear className="no-width">
                  <Row>
                    <Col xs="6">
                      <Label>{t('COMMON:TITLE_FILTER_ACC_MONTH')}</Label>
                    </Col>
                    <Col xs="6">
                      <FormGroup>
                        <CustomInput
                          type="select"
                          id="energyAccountabilityMonth"
                          name="energyAccountabilityMonth"
                          disabled={!query.energyAccountabilityYear}
                          value={
                            query && query.energyAccountabilityMonth === null
                              ? ''
                              : query.energyAccountabilityMonth
                          }
                          onChange={({
                            target: { value: energyAccountabilityMonth },
                          }) => onFilter({ energyAccountabilityMonth })}
                        >
                          {generateMonthOptions()}
                        </CustomInput>
                      </FormGroup>
                    </Col>
                  </Row>
                </ContFilterYear>
              </Col>
              <Col sm="12" md="4" lg="3" xl="3" className="select-wrapper">
                <ContFilterYear className="no-width">
                  <Row>
                    <Col xs="6">
                      <Label>Tipo cliente</Label>
                    </Col>
                    <Col xs="6">
                      <FormGroup>
                        <CustomInput
                          type="select"
                          id="customerType"
                          name="customerType"
                          value={query.customerType}
                          onChange={({ target: { value: customerType } }) =>
                            onFilter({ customerType })
                          }
                        >
                          <option value="" defaultChecked>
                            Tutti
                          </option>
                          <option value="condos">Condomini</option>
                          <option value="customerExtra">Clienti privati</option>
                        </CustomInput>
                      </FormGroup>
                    </Col>
                  </Row>
                </ContFilterYear>
              </Col>
              <Col sm="12" md="4" lg="3" xl="3" className="select-wrapper">
                <ContFilterYear className="no-width">
                  <Row>
                    <Col xs="6">
                      <Label>Tipo contratto</Label>
                    </Col>
                    <Col xs="6">
                      <FormGroup>
                        <CustomInput
                          type="select"
                          id="contractType"
                          name="contractType"
                          value={query.contractType}
                          onChange={({ target: { value: contractType } }) =>
                            onFilter({ contractType })
                          }
                        >
                          <option value="" defaultChecked>
                            Tutti
                          </option>
                          <option value="POD">POD</option>
                          <option value="PDR">PDR</option>
                        </CustomInput>
                      </FormGroup>
                    </Col>
                  </Row>
                </ContFilterYear>
              </Col>
              <Col sm="12" md="4" lg="3" xl="3" className="select-wrapper">
                <ContFilterYear className="no-width">
                  <Row>
                    <Col xs="6">
                      <Label>Voce</Label>
                    </Col>
                    <Col xs="6">
                      <FormGroup>
                        <CustomInput
                          type="select"
                          id="partnerEonType"
                          name="partnerEonType"
                          value={query.partnerEonType}
                          onChange={({ target: { value: partnerEonType } }) =>
                            onFilter({ partnerEonType })
                          }
                        >
                          <option value="">Tutti</option>
                          <option value="GETTONE">Gettone</option>
                          <option value="STORNO_GETTONE">Storno Gettone</option>
                        </CustomInput>
                      </FormGroup>
                    </Col>
                  </Row>
                </ContFilterYear>
              </Col>
            </Row>
            <br />
            <EnergyRoyaltyTable
              data={dataRevEnergy || []}
              total={total}
              query={query}
              onFilter={onFilter}
              showEmptyTableMessage={true}
              profile={profile}
              bottomRow={
                <TotalEnergyRevenuesRow
                  totals={getVariableRevenuesTotals(dataRevEnergy)}
                  route="variable-revenues-energy"
                  activeRoute={false}
                  profileType={profileType}
                />
              }
            />
            <ContPaginationPage>
              <p>{t('COMMON:PAGINATION_PER_PAGE')}</p>

              <FormGroup>
                <CustomInput
                  type="select"
                  className="pagination-select"
                  id="perPage"
                  name="perPage"
                  value={query.limit}
                  onChange={({ target: { value: limit } }) => {
                    onFilter({ limit });
                  }}
                >
                  {generatePaginationOptions()}
                </CustomInput>
              </FormGroup>
            </ContPaginationPage>
          </ContAffiliation>
        </TabPane>
      </TabContent>

      <ReportingModal
        toggle={toggle}
        isOpen={isOpen}
        onSubmit={onSubmit}
        amount={amount}
        query={query}
      />
    </Form>
  );
};

export default VarRevEnergyDetailPresentational;
